import React, { useEffect } from "react";
import { Button, IconButton, Input, Menu, MenuHandler, MenuItem, MenuList, Option, Select, Textarea, Tooltip } from "@material-tailwind/react";
import { convertToPresentableText } from "../../../utils/functions";
import { DEAL_STAGES, DEAL_STAGES_ENUM, LEVELS_ENUM, MARKETING_CHANNELS_ENUM, RELATIONSHIP_STRENGTHS_ENUM } from "../../../utils/enums";
import { useCountries } from "use-react-countries";
import { InfoIcon } from "lucide-react";
import { phoneNumberInputInstructionsTemplate } from "../../../utils/templates";

const BasicInfoTab = ({ formData, handlers, salesReps }) => {
  const {
    handleInputChange,
    handleSelectChange
  } = handlers;

  const { countries } = useCountries();
  const [country, setCountry] = React.useState(countries.findIndex(country => country.countryCallingCode === (formData?.countryCode || "+92")));
  const { name, flags, countryCallingCode } = countries[country];

  useEffect(() => {
    if (formData.countryCode !== countryCallingCode) {
      handleInputChange({ target: { name: "countryCode", value: countryCallingCode } });
    }
    if (
      formData.phoneNumber &&
      formData.phoneNumber.length > 1 &&
      formData.phoneNumber.length <= 3 &&
      (
        formData.phoneNumber.startsWith("+") ||
        formData.phoneNumber.startsWith("00") ||
        formData.phoneNumber.startsWith("0") ||
        formData.phoneNumber.startsWith("92")
      )
    ) {
      const prefix = formData.phoneNumber.startsWith("92") ? "+" : "";
      const countryCode = prefix + formData.phoneNumber;
      const countryIndex = countries.findIndex(country => country.countryCallingCode === countryCode);
      if (countryIndex !== -1) {
        handleInputChange({ target: { name: "phoneNumber", value: "" } });
        setCountry(countryIndex);
      }
    }
  }, [countryCallingCode, formData.countryCode, handleInputChange, countries, formData.phoneNumber]);

  return (
    <div className="space-y-4">

      <div className="flex gap-4 flex-col md:flex-row">
        <Select
          label="Lead Status"
          labelProps={{
            className: `after:content-['*'] after:text-red-500 after:ml-1 after:mb-[3px]`
          }}
          value={formData.leadStatus}
          onChange={(value) => handleSelectChange("leadStatus", value)}
        >
          {DEAL_STAGES_ENUM.map((status, index) => (
            <Option key={index} value={status}>
              {convertToPresentableText(status)}
            </Option>
          ))}
        </Select>

        <Select
          label="Lead Source"
          labelProps={{
            className: `after:content-['*'] after:text-red-500 after:ml-1 after:mb-[3px]`
          }}
          value={formData.leadSource}
          onChange={(value) => handleSelectChange("leadSource", value)}
          required
        >
          {MARKETING_CHANNELS_ENUM.map((source, index) => (
            <Option key={index} value={source}>
              {convertToPresentableText(source)}
            </Option>
          ))}
        </Select>

      </div>
      <div className="flex gap-4 flex-col md:flex-row">
        <Select
          label="Engagement Level"
          value={formData.engagementLevel}
          onChange={(value) => handleSelectChange("engagementLevel", value)}
        >
          {LEVELS_ENUM.map((level, index) => (
            <Option key={index} value={level}>
              {convertToPresentableText(level)}
            </Option>
          ))}
        </Select>
        <Select
          label="Purchase Intent"
          value={formData.purchaseIntent}
          onChange={(value) => handleSelectChange("purchaseIntent", value)}
        >
          {RELATIONSHIP_STRENGTHS_ENUM.map((intent, index) => (
            <Option key={index} value={intent}>
              {convertToPresentableText(intent)}
            </Option>
          ))}
        </Select>
      </div>
      <div className="flex gap-4 flex-col md:flex-row">
        <Input
          label="Required Seats"
          name="desiredSeatsCount"
          type="number"
          value={formData.desiredSeatsCount}
          onChange={handleInputChange}
        />
        <Input
          label="Required Offices"
          name="desiredOfficesCount"
          type="number"
          value={formData.desiredOfficesCount}
          onChange={handleInputChange}
        />

        <Input
          label="Connection Date"
          name="connectionDate"
          type="datetime-local"
          value={formData.connectionDate}
          onChange={handleInputChange}
        />
      </div>
      <hr />
      <div className="flex gap-4 flex-col md:flex-row">
        <Input
          label="Full Name"
          name="fullName"
          value={formData.fullName}
          onChange={handleInputChange}
          required
        />
        {/* <Input
          label="Phone Number"
          required={formData.leadStatus === DEAL_STAGES.WON}
          name="phoneNumber"
          placeholder="+92XXXXXXXXXX"
          value={formData.phoneNumber}
          onChange={handleInputChange}
        /> */}
        <div className="relative flex w-full max-w-[24rem]">
          <Menu placement="bottom-start">
            <MenuHandler >
              <Button
                ripple={false}
                variant="text"
                color="blue-gray"
                className="flex h-10 items-center gap-2 rounded-r-none border border-r-0 border-blue-gray-200 bg-blue-gray-500/10 pl-3"
              >
                <img
                  src={flags.svg}
                  alt={name}
                  className="h-4 w-4 rounded-full object-cover"
                />
                {countryCallingCode}
              </Button>
            </MenuHandler>
            <MenuList className="max-h-[20rem] max-w-[18rem] z-[10000]">
              {countries.map(({ name, flags, countryCallingCode }, index) => {
                return (
                  <MenuItem
                    key={name}
                    value={name}
                    className="flex items-center gap-2"
                    onClick={() => setCountry(index)}
                  >
                    <img
                      src={flags.svg}
                      alt={name}
                      className="h-5 w-5 rounded-full object-cover"
                    />
                    {name} <span className="ml-auto">{countryCallingCode}</span>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
          <Input
            type="tel"
            placeholder="Mobile Number"
            className="rounded-l-none !border-t-blue-gray-200 focus:!border-t-gray-900"
            required={formData.leadStatus === DEAL_STAGES.WON}
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            labelProps={{ className: "before:content-none after:content-none" }}
            containerProps={{ className: "min-w-0" }}
            icon={<Tooltip content={phoneNumberInputInstructionsTemplate} placement="top" className="z-[10000]">
              <IconButton className="rounded-full h-4 w-4" size="sm">
                <InfoIcon size={14} />
              </IconButton>
            </Tooltip>}
          />
        </div>

      </div>

      <div className="flex gap-4 flex-col md:flex-row">
        <Input
          label="Email Address"
          name="emailAddress"
          type="email"
          value={formData.emailAddress}
          required={formData.leadStatus === DEAL_STAGES.WON}
          onChange={handleInputChange}
        />
        <Input
          label="Secondary Email"
          name="secondaryEmail"
          type="email"
          value={formData.secondaryEmail}
          onChange={handleInputChange}
        />
        <Input
          label="CNIC"
          name="cnic"
          value={formData.cnic}
          required={formData.leadStatus === DEAL_STAGES.WON}
          onChange={handleInputChange}
        />
      </div>

      <div className="flex gap-4 flex-col md:flex-row">
        <Input
          label="Contact Person Job Title"
          name="jobTitle"
          value={formData.jobTitle}
          onChange={handleInputChange}
        />
        <Input
          label="Additional Mobile Number"
          name="mobileNumber"
          value={formData.mobileNumber}
          onChange={handleInputChange}
        />
      </div>



      <div className="w-full">
        <Textarea
          label="Additional Notes (Optional)"
          name="notes"
          value={formData.notes || ''}
          onChange={handleInputChange}
        />
      </div>
      <hr />
      <div className="flex gap-4 flex-col md:flex-row">
        <Select
          label="Assigned Sales Rep"
          value={formData.assignedSalesRep}
          onChange={(value) => handleSelectChange("assignedSalesRep", value)}
        >
          {salesReps?.map((rep) => (
            <Option key={rep._id} value={rep._id}>
              {rep.name}
            </Option>
          ))}
        </Select>
        <Input
          label="Assignment Date"
          name="assignmentDate"
          type="date"
          value={formData.assignmentDate ? new Date(formData.assignmentDate).toISOString().split('T')[0] : ''}
          onChange={handleInputChange}
        />

      </div>

    </div>
  );
};

export default BasicInfoTab;